import {
    AnyAction,
    CaseReducer,
    Draft,
    createAction,
    createAsyncThunk,
} from '@reduxjs/toolkit'
import {
    NinchatActionType,
    NinchatSendMessageAction,
} from '../types/actions'
import {
    NinchatState,
    NinchatStore,
} from 'api/types'
import ninchatClient, {
    NinchatActionParams,
    NinchatActionPayload,
    NinchatClientPromiseValue,
} from '../NinchatClient'
import {NinchatMessageTypes} from '../types/messages'
import {messageReceived} from '../event/messageReceived'

const actionTypePrefix = 'NINCHAT/action'

export const removePendingMessage = createAction<{chatId: string, messageId: string}>(`${actionTypePrefix}/removePendingMessage`)

type MetaReducerAsyncThunkConfig = {
    fulfilledMeta: {reducer: CaseReducer},
    pendingMeta: {reducer: CaseReducer},
    rejectedMeta: {reducer: CaseReducer},
}

type SendMessageParams = NinchatActionParams<NinchatSendMessageAction> & NinchatActionPayload & {retry_message_id?: string}

export const sendMessage = createAsyncThunk<NinchatClientPromiseValue, SendMessageParams, MetaReducerAsyncThunkConfig>(
    `${actionTypePrefix}/${NinchatActionType.NinchatSendMessageAction}`,
    async (arg, thunkAPI) => {
        const {
            payload,
            ...params
        } = arg

        delete params.retry_message_id

        params.action = NinchatActionType.NinchatSendMessageAction
        try {
            const reply = await ninchatClient.send(params, payload)

            return thunkAPI.fulfillWithValue(reply, {reducer: messageReceivedReducer}) // eslint-disable-line @typescript-eslint/no-use-before-define
        } catch (err) {
            if (err === 'not connected') {
                await new Promise((resolve) => {
                    setTimeout(resolve, 300)
                })
            }
            return thunkAPI.rejectWithValue(err, {reducer: rejectedSendMessageReducer}) // eslint-disable-line @typescript-eslint/no-use-before-define
        }
    },
    {getPendingMeta: () => ({reducer: pendingSendMessageReducer})}, // eslint-disable-line @typescript-eslint/no-use-before-define
)

const messageReceivedReducer = (state: Draft<NinchatState>, action: AnyAction) => {
    messageReceived.reducer(state, {
        ...action,
        meta: {
            ...messageReceived.meta(state, action.payload),
            requestId: action.meta.requestId,
            requestStatus: action.meta.requestStatus,
        },
    })
}

const pendingSendMessageReducer = (state: Draft<NinchatState>, action: AnyAction) => {
    const {
        arg: {
            payload: messagePayload,
            retry_message_id,
            ...sendParams
        },
        requestId,
        requestStatus,
    } = action.meta

    const {
        channel_id,
        message_type,
        user_id,
    } = sendParams

    if (message_type === NinchatMessageTypes.NinchatMetadataMessage && sendParams.message_fold) {
        return
    }

    const message: NinchatStore.NinchatStoreMessage = {
        ...JSON.parse(messagePayload),
        _message_status: requestStatus,
        _revision: Date.now(),
        _send_params: {
            ...sendParams,
            payload: messagePayload,
        },
        message_id: requestId,
        message_time: Math.floor(Date.now() / 1000),
        message_type,
        message_user_id: state.user_id,
        message_user_name: state.user_attrs.name,
    }

    const chatId = channel_id ?? user_id

    if (chatId) {
        state._messages[chatId] ??= {messages: []}

        const oldIndex = retry_message_id ? state._messages[chatId].messages.findIndex((m) => m.message_id === retry_message_id) : -1
        if (oldIndex >= 0) {
            // A retry is another attempt at the same visible message. Keep its
            // original position while replacing the request ID used for replies.
            message.message_time = state._messages[chatId].messages[oldIndex].message_time
            state._messages[chatId].messages[oldIndex] = message
        } else {
            state._messages[chatId].messages.push(message)
        }
    }
}

const rejectedSendMessageReducer = (state: Draft<NinchatState>, action: AnyAction) => {
    const {
        arg: {
            channel_id,
            user_id,
        },
        requestId,
        requestStatus,
    } = action.meta

    const chatId = channel_id ?? user_id

    const messages = chatId ? state._messages[chatId]?.messages : undefined

    if (!messages) {
        return
    }

    const message = messages.find((m: NinchatStore.NinchatStoreMessage) => m.message_id === requestId)

    if (message) {
        message._message_status = requestStatus
        message._revision = Date.now()
    }
}
